/**
 * 3D jar model — renders the real ElectroShockoLyty label artwork
 * (label-wrap.png, logo-badge.png) wrapped around a three-part cylinder
 * (base / labelled body / lid), instead of a flat product photo.
 *
 * Mounts on every [data-jar3d] element found in the page. Auto-rotates
 * idly; elements with data-jar-drag="true" also accept pointer drag.
 * Respects prefers-reduced-motion (renders one static frame, no loop).
 *
 * Texture URLs: Shopify serves this file from a CDN asset URL, so a plain
 * relative path like 'assets/label-wrap.png' would resolve against the
 * *page's* URL, not the theme's assets folder, and 404. templates/
 * page.custom-home.liquid sets window.electroShockoLytyAssets with the
 * real {{ ... | asset_url }} URLs before this module loads; the plain
 * relative paths remain as a fallback so this same file still works
 * unmodified on the original static (non-Shopify) site.
 */
import * as THREE from 'three';

const shopifyAssets = window.electroShockoLytyAssets || {};
const LABEL_URL = shopifyAssets.labelUrl || 'assets/label-wrap.png';
const BADGE_URL = shopifyAssets.badgeUrl || 'assets/logo-badge.png';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// rotation.y = Math.PI faces the main "$HOCKO LYTY" wordmark straight at the
// camera (rotation.y = 0 instead faces the ingredients/nutrition-table side
// of the label — verified empirically against the live label-wrap.png UV
// wrap, not assumed). The jar sways gently either side of this angle instead
// of spinning a full 360°, so the front label stays on screen at all times.
const FRONT_ANGLE = Math.PI;
const SWAY_RANGE = (15 * Math.PI) / 180; // ±15°
const SWAY_ANGULAR_FREQ = (2 * Math.PI) / 7000; // one full back-and-forth every ~7s

function buildJar(labelTex, badgeTex) {
  const group = new THREE.Group();

  const radius = 0.78;
  const bodyHeight = 0.95;
  const lidHeight = 0.4;
  const baseHeight = 0.14;
  const totalHeight = bodyHeight + lidHeight + baseHeight;

  const plasticMat = new THREE.MeshStandardMaterial({
    color: 0x121415,
    roughness: 0.4,
    metalness: 0.15,
  });
  const labelMat = new THREE.MeshStandardMaterial({
    map: labelTex,
    roughness: 0.55,
    metalness: 0.05,
  });
  const badgeMat = new THREE.MeshStandardMaterial({
    map: badgeTex,
    roughness: 0.4,
    metalness: 0.05,
  });

  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius * 1.02, baseHeight, 56),
    plasticMat
  );
  base.position.y = -totalHeight / 2 + baseHeight / 2;
  group.add(base);

  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, bodyHeight, 56, 1, false),
    [labelMat, plasticMat, plasticMat]
  );
  body.position.y = -totalHeight / 2 + baseHeight + bodyHeight / 2;
  group.add(body);

  const lid = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 1.03, radius * 1.03, lidHeight, 56, 1, false),
    [plasticMat, badgeMat, plasticMat]
  );
  lid.position.y = -totalHeight / 2 + baseHeight + bodyHeight + lidHeight / 2;
  group.add(lid);

  return group;
}

function mount(container) {
  const size = parseFloat(container.dataset.jarSize) || 1;
  const draggable = container.dataset.jarDrag === 'true';

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.set(0, 1.05, 4.3);
  camera.lookAt(0, -0.15, 0);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);

  scene.add(new THREE.AmbientLight(0xffffff, 0.55));

  const key = new THREE.DirectionalLight(0xffffff, 1.1);
  key.position.set(2.2, 3, 2.5);
  scene.add(key);

  const rim = new THREE.PointLight(0x5fc9e8, 3.2, 12, 2);
  rim.position.set(-2.4, 1.2, -1.8);
  scene.add(rim);

  const fill = new THREE.DirectionalLight(0x8ddcf0, 0.25);
  fill.position.set(-1.5, -1, 2);
  scene.add(fill);

  const loader = new THREE.TextureLoader();
  let jar = null;

  Promise.all([
    new Promise((res) => loader.load(LABEL_URL, res)),
    new Promise((res) => loader.load(BADGE_URL, res)),
  ]).then(([labelTex, badgeTex]) => {
    labelTex.colorSpace = THREE.SRGBColorSpace;
    badgeTex.colorSpace = THREE.SRGBColorSpace;
    labelTex.anisotropy = renderer.capabilities.getMaxAnisotropy();

    jar = buildJar(labelTex, badgeTex);
    jar.scale.setScalar(size);
    jar.rotation.y = FRONT_ANGLE;
    scene.add(jar);
    render();
    if (!reduceMotion) requestAnimationFrame(animate);
  });

  function resize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    render();
  }

  function render() {
    renderer.render(scene, camera);
  }

  let dragging = false;
  let lastX = 0;
  let idleResume = 0;
  let swayPhase = 0; // radians; rotation.y = FRONT_ANGLE + sin(swayPhase) * SWAY_RANGE
  let lastFrameTime = null;

  if (draggable) {
    container.addEventListener('pointerdown', (e) => {
      dragging = true;
      lastX = e.clientX;
      container.setPointerCapture(e.pointerId);
    });
    container.addEventListener('pointermove', (e) => {
      if (!dragging || !jar) return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      // Clamped to the same ±15° range as the idle sway — dragging can
      // reveal a bit more of the side label, but never spins to the back.
      const next = jar.rotation.y + dx * 0.01;
      jar.rotation.y = Math.min(
        FRONT_ANGLE + SWAY_RANGE,
        Math.max(FRONT_ANGLE - SWAY_RANGE, next)
      );
      render();
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((evt) =>
      container.addEventListener(evt, () => {
        dragging = false;
        idleResume = performance.now() + 1600;
        // Re-sync the sway's phase to wherever the drag left the jar, so
        // the idle animation resumes smoothly instead of jumping.
        if (jar) {
          const ratio = Math.min(1, Math.max(-1, (jar.rotation.y - FRONT_ANGLE) / SWAY_RANGE));
          swayPhase = Math.asin(ratio);
        }
      })
    );
  }

  function animate(t) {
    if (lastFrameTime === null) lastFrameTime = t;
    const dt = t - lastFrameTime;
    lastFrameTime = t;

    if (jar && !dragging && (idleResume === 0 || t > idleResume)) {
      swayPhase += dt * SWAY_ANGULAR_FREQ;
      jar.rotation.y = FRONT_ANGLE + Math.sin(swayPhase) * SWAY_RANGE;
    }
    render();
    requestAnimationFrame(animate);
  }

  new ResizeObserver(resize).observe(container);
  resize();
}

document.querySelectorAll('[data-jar3d]').forEach(mount);
